import { useMemo } from 'react';
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { ChartContainer } from '@Components/ChartContainer';
import { ChartTooltipV2, KeyShapeOptions } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Neutral400, Neutral500, Neutral600 } from '@src/utils/colors';
import { formatCurrency, formatWholeNumber } from '../../utils/dtc-commerce-mmm-format';
import { findYearBoundaries, TimeAggConst } from '../../utils/dtc-commerce-mmm-year-markers';
import {
  useElementWidth,
  YEAR_MARKER_STROKE,
  YearMarkerLabel
} from '../../utils/dtc-commerce-mmm-year-marker-ui';
import { EXPANDED_CHART_HEIGHT, ExpandableChartName } from './channel-drawer-constants';
import { DownloadActions, TSPoint } from './channel-drawer-types';
import { axisFontSizeFor } from './channel-drawer-utils';
import { ChannelDrawerExpandableChart } from './ChannelDrawerExpandableChart';

interface ChannelDrawerTimeSeriesProps {
  data: TSPoint[];
  isRevenue: boolean;
  focusedLabel?: string;
  revenueColor: string;
  download: DownloadActions;
}

interface TooltipPayloadItem {
  payload?: TSPoint;
}

interface TimeSeriesTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  isRevenue: boolean;
  revenueColor: string;
}

const TimeSeriesTooltip = ({
  active,
  payload,
  isRevenue,
  revenueColor
}: TimeSeriesTooltipProps) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const kpiLabel = isRevenue ? 'Revenue' : 'Conversions';
  const fmt = isRevenue ? formatCurrency : formatWholeNumber;
  const dateLabel = new Date(`${row.date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  return (
    <ChartTooltipV2
      headers={[dateLabel]}
      items={[
        {
          label: 'Spend',
          value: formatCurrency(row.spend),
          color: Neutral500,
          keyShape: KeyShapeOptions.LINE
        },
        {
          label: kpiLabel,
          value: fmt(row.contribution),
          color: revenueColor,
          keyShape: KeyShapeOptions.LINE
        }
      ]}
    />
  );
};

const TREND_HEIGHT = 260;
const TREND_MARGIN_LEFT = 8;
const TREND_MARGIN_RIGHT = 16;
const TREND_Y_AXIS_WIDTH = 64;

export const ChannelDrawerTimeSeries = ({
  data,
  isRevenue,
  focusedLabel,
  revenueColor,
  download
}: ChannelDrawerTimeSeriesProps) => {
  if (data.length === 0) return null;

  const kpiLabel = isRevenue ? 'Revenue' : 'Conversions';
  const subtitle = focusedLabel
    ? `Showing ${focusedLabel}`
    : `Weekly spend & ${kpiLabel.toLowerCase()}`;

  return (
    <ChannelDrawerExpandableChart chartName={ExpandableChartName.TREND}>
      {(isExpanded, expandButton) => (
        <ChartContainer
          className='channel-drawer-time-series'
          title={
            <div className='dtc-commerce-mmm-title-stack'>
              <div>Trend</div>
              <div className='dtc-commerce-mmm-title-stack-sub'>{subtitle}</div>
            </div>
          }
          viewRightActions
          rightActions={
            <div className='channel-drawer-section-right-actions'>
              <DownloadDropdown
                onClickOptions={[download.onExcel, () => download.onPng(isExpanded)]}
              />
              {expandButton}
            </div>
          }
        >
          <TrendChart
            data={data}
            isRevenue={isRevenue}
            kpiLabel={kpiLabel}
            revenueColor={revenueColor}
            height={isExpanded ? EXPANDED_CHART_HEIGHT : TREND_HEIGHT}
            axisFontSize={axisFontSizeFor(isExpanded)}
          />
        </ChartContainer>
      )}
    </ChannelDrawerExpandableChart>
  );
};

interface TrendChartProps {
  data: TSPoint[];
  isRevenue: boolean;
  kpiLabel: string;
  revenueColor: string;
  height: number;
  axisFontSize: number;
}

// Its own component so the drawer copy and the enlarged copy each measure their own width for the
// year-marker labels.
const TrendChart = ({
  data,
  isRevenue,
  kpiLabel,
  revenueColor,
  height,
  axisFontSize
}: TrendChartProps) => {
  const yearBoundaries = useMemo(
    () =>
      findYearBoundaries(
        data.map(r => r.date),
        TimeAggConst.WEEKLY
      ),
    [data]
  );

  const { ref: chartRef, width: chartWidth } = useElementWidth();

  return (
    <div className='channel-drawer-chart-stack' ref={chartRef}>
      <ResponsiveContainer width='100%' height={height}>
        <LineChart
          data={data}
          margin={{
            top: 24,
            right: TREND_MARGIN_RIGHT,
            bottom: 0,
            left: TREND_MARGIN_LEFT
          }}
        >
          <XAxis dataKey='date' tick={false} tickLine={false} axisLine={{ stroke: Neutral400 }} />
          <YAxis
            tickFormatter={v => formatCurrency(v)}
            tick={{ fontSize: axisFontSize, fill: Neutral600 }}
            width={TREND_Y_AXIS_WIDTH}
            axisLine={{ stroke: Neutral400 }}
            tickLine={false}
          />
          {yearBoundaries.map(({ dateKey, year }) => (
            <ReferenceLine
              key={`yr-${dateKey}`}
              x={dateKey}
              stroke={YEAR_MARKER_STROKE}
              strokeDasharray='4 4'
              strokeWidth={1}
              label={
                <YearMarkerLabel
                  year={year}
                  chartWidth={chartWidth}
                  marginLeft={TREND_MARGIN_LEFT}
                  marginRight={TREND_MARGIN_RIGHT}
                  yAxisWidth={TREND_Y_AXIS_WIDTH}
                />
              }
            />
          ))}
          <Tooltip
            content={<TimeSeriesTooltip isRevenue={isRevenue} revenueColor={revenueColor} />}
            cursor={{ stroke: Neutral500, strokeWidth: 1, strokeDasharray: '4 3' }}
          />
          <Line
            type='monotone'
            dataKey='spend'
            stroke={Neutral500}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type='monotone'
            dataKey='contribution'
            stroke={revenueColor}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className='channel-drawer-legend'>
        <div className='channel-drawer-legend-item'>
          <span className='channel-drawer-legend-swatch' style={{ backgroundColor: Neutral500 }} />
          Spend
        </div>
        <div className='channel-drawer-legend-item'>
          <span
            className='channel-drawer-legend-swatch'
            style={{ backgroundColor: revenueColor }}
          />
          {kpiLabel}
        </div>
      </div>
    </div>
  );
};
